// frontend/src/components/ui.jsx
// Shared UI primitives — used across all pages

// ─── Status badge ──────────────────────────────────────────────────────────────

const STATUS_STYLES = {
  // Agreement statuses
  DRAFT: "bg-gray-100 text-gray-700",
  REVISED: "bg-blue-100 text-blue-700",
  UNDER_REVIEW: "bg-blue-100 text-blue-700",
  REVISION_REQUESTED: "bg-amber-100 text-amber-700",
  APPROVED: "bg-green-100 text-green-700",
  EXPIRED: "bg-gray-200 text-gray-500",
  TERMINATED: "bg-red-100 text-red-700",
  VOID_ADDENDUM: "bg-purple-100 text-purple-700",
  // Collaborative flow
  DRAFTING_VENDOR_TURN: "bg-sky-100 text-sky-700",
  DRAFTING_LEGAL_TURN: "bg-indigo-100 text-indigo-700",
  AWAITING_VENDOR_AGREEMENT: "bg-amber-100 text-amber-700",
  ACTIVE: "bg-green-100 text-green-700",
  // Proposal statuses
  PENDING: "bg-yellow-100 text-yellow-800",
  ACCEPTED: "bg-green-100 text-green-800",
  REJECTED: "bg-red-100 text-red-700",
  // Payment
  UNPAID: "bg-red-100 text-red-700",
  PARTIAL: "bg-amber-100 text-amber-700",
  PAID: "bg-green-100 text-green-700",
  // Vendor
  VERIFIED: "bg-green-100 text-green-700",
  UNVERIFIED: "bg-amber-100 text-amber-700",
};

const STATUS_LABELS = {
  REVISED: "Revised",
  UNDER_REVIEW: "Under Review",
  REVISION_REQUESTED: "Needs Revision",
  VOID_ADDENDUM: "Void (Addendum)",
  DRAFTING_VENDOR_TURN: "Menunggu Vendor",
  DRAFTING_LEGAL_TURN: "Menunggu Legal",
  AWAITING_VENDOR_AGREEMENT: "Menunggu Persetujuan",
  ACTIVE: "Aktif",
  PENDING: "Menunggu Review",
  ACCEPTED: "Diterima",
  REJECTED: "Ditolak",
};

export function StatusBadge({ status }) {
  const style = STATUS_STYLES[status] ?? "bg-gray-100 text-gray-600";
  const label = STATUS_LABELS[status] ?? status?.replace(/_/g, " ");
  return <span className={`badge ${style}`}>{label}</span>;
}

// ─── Page header ──────────────────────────────────────────────────────────────

export function PageHeader({ title, subtitle, action }) {
  return (
    <div className="flex items-start justify-between mb-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-gray-500">{subtitle}</p>}
      </div>
      {action && <div className="ml-4 flex-shrink-0">{action}</div>}
    </div>
  );
}

// ─── Spinner ──────────────────────────────────────────────────────────────────

export function Spinner({ className = "" }) {
  return (
    <div className={`flex items-center justify-center py-12 ${className}`}>
      <div className="w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────

export function EmptyState({ title, description, action }) {
  return (
    <div className="text-center py-16">
      <p className="text-gray-300 text-5xl mb-4">📭</p>
      <h3 className="text-base font-medium text-gray-900">{title}</h3>
      {description && (
        <p className="mt-1 text-sm text-gray-500">{description}</p>
      )}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

// ─── Error message ────────────────────────────────────────────────────────────

export function ErrorMessage({ error }) {
  const msg =
    typeof error === "string"
      ? error
      : error?.message || error?.data?.error || "Something went wrong";

  return (
    <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
      {msg}
    </div>
  );
}

// ─── Form field wrapper ───────────────────────────────────────────────────────

export function FormField({ label, error, required, children }) {
  return (
    <div>
      {label && (
        <label className="label">
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}
      {children}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}

// ─── Document type label (Indonesian) ─────────────────────────────────────────

const DOC_LABELS = {
  AKTA: "Akta Pendirian",
  AKTA_PERUBAHAN: "Akta Perubahan",
  SK_KEMENKUMHAM: "SK Kemenkumham",
  NPWP: "NPWP",
  SHAREHOLDER_STRUCTURE: "Struktur Pemegang Saham",
  LAPORAN_KEUANGAN: "Laporan Keuangan",
  // Perizinan
  HAKI: "HAKI",
  NIB: "NIB",
  PKKPR: "PKKPR",
  SERTIFIKAT_HALAL: "Sertifikat Halal",
  PBG: "PBG",
  SLF: "SLF",
  LEGALITAS_LAHAN: "Legalitas Lahan",
  LAPORAN_RUPS_LUAR_BIASA: "Laporan RUPS Luar Biasa",
  LAPORAN_RUPS_TAHUNAN: "Laporan RUPS Tahunan",
};

export function DocTypeLabel({ type, customName }) {
  if (type === "CUSTOM" && customName) return <span>{customName}</span>;
  return <span>{DOC_LABELS[type] ?? type}</span>;
}

// ─── Utility formatters ───────────────────────────────────────────────────────

export function formatDate(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export function formatFileSize(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
